import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Group,
  PasswordInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useTutorialsAvailable } from "../hooks/useTutorialsAvailable";
import { getCurrentUser, isDistributor } from "../shared/getCurrentUser";
import { ApiError, RestApi } from "../shared/RestApi";
import { NavBarContainer } from "./NavBarContainer";

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Group
      justify="space-between"
      align="flex-start"
      wrap="wrap"
      gap="sm"
      py="sm"
    >
      <Text size="sm" c="dimmed" fw={500} miw={140}>
        {label}
      </Text>
      <Box style={{ flex: 1, textAlign: "right" }}>{children}</Box>
    </Group>
  );
}

function subscriptionStatusLabel(
  t: TFunction,
  status: string | undefined,
): string {
  switch ((status ?? "").toLowerCase()) {
    case "active":
      return t("userInfo.statusActive");
    case "trialing":
      return t("userInfo.statusTrialing");
    case "past_due":
      return t("userInfo.statusPastDue");
    case "canceled":
      return t("userInfo.statusCanceled");
    case "unpaid":
      return t("userInfo.statusUnpaid");
    case "incomplete":
      return t("userInfo.statusIncomplete");
    case "incomplete_expired":
      return t("userInfo.statusIncompleteExpired");
    case "paused":
      return t("userInfo.statusPaused");
    default:
      return t("userInfo.statusUnknown", { status: status || "—" });
  }
}

function subscriptionBadgeColor(status: string | undefined): string {
  switch ((status ?? "").toLowerCase()) {
    case "active":
    case "trialing":
      return "brand";
    case "past_due":
      return "orange";
    case "canceled":
    case "unpaid":
    case "incomplete_expired":
      return "red";
    default:
      return "charcoal";
  }
}

function formatPeriodEnd(iso: string | undefined, locale: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function UserInfoPage() {
  const { t, i18n } = useTranslation();
  const showTutorials = useTutorialsAvailable();
  const user = getCurrentUser()!;
  const queryClient = useQueryClient();
  const [resendMsg, setResendMsg] = React.useState<string | null>(null);
  const [resendOk, setResendOk] = React.useState(false);
  const [passwordFormOpen, setPasswordFormOpen] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [passwordMsg, setPasswordMsg] = React.useState<string | null>(null);
  const [passwordOk, setPasswordOk] = React.useState(false);
  const hasSubscriptionRecord = Boolean(user.StripeSubscriptionId);

  const passwordStatusQuery = useQuery({
    queryKey: ["localAuthHasPassword"],
    queryFn: () => RestApi.localAuthHasPassword(),
  });

  const subscriptionQuery = useQuery({
    queryKey: ["subscriptionStatus", user.StripeSubscriptionId],
    queryFn: () => RestApi.getSubscriptionStatus(),
    enabled: hasSubscriptionRecord,
  });

  const resendMutation = useMutation({
    mutationFn: () => RestApi.resendConfirmationEmail(),
    onSuccess: () => {
      setResendOk(true);
      setResendMsg(t("userInfo.resentEmail", { email: user.Email }));
    },
    onError: () => {
      setResendOk(false);
      setResendMsg(t("userInfo.resendError"));
    },
  });

  const hasPassword = passwordStatusQuery.data === true;

  const passwordMutation = useMutation({
    mutationFn: async () => {
      if (hasPassword) {
        await RestApi.localAuthChangePassword(currentPassword, newPassword);
      } else {
        await RestApi.localAuthSetPassword(newPassword);
      }
    },
    onSuccess: async () => {
      setPasswordOk(true);
      setPasswordMsg(
        hasPassword
          ? t("userInfo.passwordChanged")
          : t("userInfo.passwordSet"),
      );
      setCurrentPassword("");
      setNewPassword("");
      await queryClient.invalidateQueries({ queryKey: ["localAuthHasPassword"] });
    },
    onError: (err: unknown) => {
      setPasswordOk(false);
      const message =
        err instanceof ApiError ? err.message : t("userInfo.passwordError");
      setPasswordMsg(message);
    },
  });

  return (
    <NavBarContainer fullBleed showFooter>
      <Box
        style={{
          background:
            "linear-gradient(160deg, #fff8f2 0%, #ffffff 45%, #fff4eb 100%)",
          minHeight: "calc(100vh - 60px - 160px)",
        }}
        py={{ base: "xl", md: 48 }}
      >
        <Container size="md">
          <Stack gap="xl">
            <Title order={1} c="charcoal.8">
              {t("userInfo.title")}
            </Title>

            <Box
              p="xl"
              style={{
                background: "white",
                border: "1px solid var(--mantine-color-gray-2)",
                borderRadius: "var(--mantine-radius-md)",
                boxShadow: "0 8px 32px rgba(44, 45, 48, 0.06)",
              }}
            >
              <Stack gap={0}>
                <Group justify="space-between" align="center" mb="md" wrap="wrap">
                  <Title order={3} c="charcoal.8">
                    {t("userInfo.detailsTitle")}
                  </Title>
                  {hasSubscriptionRecord ? (
                    <Badge
                      color={subscriptionBadgeColor(subscriptionQuery.data?.Status)}
                      variant="light"
                      size="lg"
                    >
                      {subscriptionQuery.isLoading
                        ? t("userInfo.subscription")
                        : subscriptionStatusLabel(t, subscriptionQuery.data?.Status)}
                    </Badge>
                  ) : user.Bought ? (
                    <Badge color="brand" variant="light" size="lg">
                      {t("userInfo.lifetimeAccess")}
                    </Badge>
                  ) : (
                    <Badge color="charcoal" variant="light" size="lg">
                      {t("userInfo.freeVersion")}
                    </Badge>
                  )}
                </Group>

                <DetailRow label={t("userInfo.email")}>
                  <Text fw={500}>{user.Email}</Text>
                </DetailRow>
                <Divider color="gray.1" />
                <DetailRow label={t("userInfo.emailConfirmed")}>
                  <Group gap="sm" justify="flex-end" wrap="wrap">
                    {user.EmailConfirmed ? (
                      <Badge
                        color="green"
                        variant="light"
                        leftSection={<IconCheck size={12} />}
                      >
                        {t("userInfo.confirmed")}
                      </Badge>
                    ) : (
                      <>
                        <Badge
                          color="orange"
                          variant="light"
                          leftSection={<IconX size={12} />}
                        >
                          {t("userInfo.notConfirmed")}
                        </Badge>
                        <Button
                          size="xs"
                          color="brand"
                          loading={resendMutation.isPending}
                          onClick={() => resendMutation.mutate()}
                        >
                          {t("userInfo.resend")}
                        </Button>
                      </>
                    )}
                  </Group>
                </DetailRow>
                <Divider color="gray.1" />
                <DetailRow label={t("userInfo.userId")}>
                  <Text size="sm" c="dimmed" style={{ wordBreak: "break-all" }}>
                    {user.UserId}
                  </Text>
                </DetailRow>
                <Divider color="gray.1" />
                <DetailRow label={t("userInfo.version")}>
                  {hasSubscriptionRecord ? (
                    <Stack gap={2} align="flex-end">
                      <Text fw={600} c="brand.7">
                        {t("userInfo.subscription")}
                      </Text>
                      {user.PaymentAmount > 0 && (
                        <Text size="xs" c="dimmed">
                          USD ${user.PaymentAmount.toFixed(2)}
                          {user.PaymentCurrency
                            ? ` ${user.PaymentCurrency}`
                            : ""}
                          /mo
                        </Text>
                      )}
                    </Stack>
                  ) : user.Bought ? (
                    <Stack gap={2} align="flex-end">
                      <Text fw={600} c="brand.7">
                        {t("userInfo.lifetimeAccess")}
                      </Text>
                      {user.PaymentId && (
                        <Text size="xs" c="dimmed">
                          {user.PaymentMethod}: {user.PaymentId}
                        </Text>
                      )}
                    </Stack>
                  ) : (
                    <Stack gap="xs" align="flex-end">
                      <Text fw={500}>{t("userInfo.freeVersion")}</Text>
                      <Button component={Link} to="/buy" size="sm" color="brand">
                        {t("userInfo.buyApp")}
                      </Button>
                    </Stack>
                  )}
                </DetailRow>
                <Divider color="gray.1" />
                <DetailRow label={t("userInfo.accountType")}>
                  <Text size="sm">
                    {user.AccountType}
                    {user.AccountEmail ? `: ${user.AccountEmail}` : ""}
                  </Text>
                </DetailRow>
              </Stack>
            </Box>

            {hasSubscriptionRecord && (
              <Box
                p="xl"
                style={{
                  background: "white",
                  border: "1px solid var(--mantine-color-gray-2)",
                  borderRadius: "var(--mantine-radius-md)",
                  boxShadow: "0 8px 32px rgba(44, 45, 48, 0.06)",
                }}
              >
                <Group justify="space-between" align="center" mb="md" wrap="wrap">
                  <Title order={3} c="charcoal.8">
                    {t("userInfo.subscriptionTitle")}
                  </Title>
                  {user.StripeCustomerId && (
                    <Button
                      component="a"
                      href="/buy/billingportal"
                      size="sm"
                      color="brand"
                      variant="light"
                    >
                      {t("userInfo.manageBilling")}
                    </Button>
                  )}
                </Group>

                {subscriptionQuery.isLoading && (
                  <Text size="sm" c="dimmed">
                    {t("userInfo.subscriptionLoading")}
                  </Text>
                )}

                {subscriptionQuery.isError && (
                  <Alert color="red" variant="light">
                    {t("userInfo.subscriptionError")}
                  </Alert>
                )}

                {subscriptionQuery.data && (
                  <Stack gap="md">
                    <Stack gap={0}>
                      <DetailRow label={t("userInfo.subscriptionStatus")}>
                        <Badge
                          color={subscriptionBadgeColor(
                            subscriptionQuery.data.Status,
                          )}
                          variant="light"
                        >
                          {subscriptionStatusLabel(
                            t,
                            subscriptionQuery.data.Status,
                          )}
                        </Badge>
                      </DetailRow>
                      {formatPeriodEnd(
                        subscriptionQuery.data.CurrentPeriodEnd,
                        i18n.language,
                      ) && (
                        <>
                          <Divider color="gray.1" />
                          <DetailRow
                            label={
                              subscriptionQuery.data.CancelAtPeriodEnd ||
                              !subscriptionQuery.data.HasAccess
                                ? t("userInfo.subscriptionEnds")
                                : t("userInfo.subscriptionRenews")
                            }
                          >
                            <Text fw={500}>
                              {formatPeriodEnd(
                                subscriptionQuery.data.CurrentPeriodEnd,
                                i18n.language,
                              )}
                            </Text>
                          </DetailRow>
                        </>
                      )}
                    </Stack>
                    {subscriptionQuery.data.CancelAtPeriodEnd && (
                      <Alert color="orange" variant="light">
                        {t("userInfo.subscriptionCancelScheduled")}
                      </Alert>
                    )}
                  </Stack>
                )}
              </Box>
            )}

            {resendMsg && (
              <Alert
                color={resendOk ? "green" : "red"}
                variant="light"
              >
                {resendMsg}
              </Alert>
            )}

            <Box
              p="xl"
              style={{
                background: "white",
                border: "1px solid var(--mantine-color-gray-2)",
                borderRadius: "var(--mantine-radius-md)",
                boxShadow: "0 8px 32px rgba(44, 45, 48, 0.06)",
              }}
            >
              <Title order={3} c="charcoal.8" mb="xs">
                {hasPassword
                  ? t("userInfo.changePasswordTitle")
                  : t("userInfo.setPasswordTitle")}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {hasPassword
                  ? t("userInfo.changePasswordIntro")
                  : t("userInfo.setPasswordIntro")}
              </Text>
              {!user.EmailConfirmed && !hasPassword ? (
                <Alert color="orange" variant="light">
                  {t("userInfo.confirmEmailBeforePassword")}
                </Alert>
              ) : !passwordFormOpen ? (
                <Button
                  color="brand"
                  variant="light"
                  loading={passwordStatusQuery.isLoading}
                  onClick={() => {
                    setPasswordMsg(null);
                    setPasswordFormOpen(true);
                  }}
                >
                  {hasPassword
                    ? t("userInfo.changePasswordExpand")
                    : t("userInfo.setPasswordExpand")}
                </Button>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setPasswordMsg(null);
                    passwordMutation.mutate();
                  }}
                >
                  <Stack gap="sm">
                    {hasPassword ? (
                      <PasswordInput
                        label={t("userInfo.currentPassword")}
                        autoComplete="current-password"
                        required
                        value={currentPassword}
                        onChange={(e) =>
                          setCurrentPassword(e.currentTarget.value)
                        }
                      />
                    ) : null}
                    <PasswordInput
                      label={t("userInfo.newPassword")}
                      description={t("userInfo.passwordHint")}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.currentTarget.value)}
                    />
                    {passwordMsg ? (
                      <Alert
                        color={passwordOk ? "green" : "red"}
                        variant="light"
                      >
                        {passwordMsg}
                      </Alert>
                    ) : null}
                    <Group gap="sm">
                      <Button
                        type="submit"
                        color="brand"
                        loading={passwordMutation.isPending}
                        disabled={passwordStatusQuery.isLoading}
                      >
                        {hasPassword
                          ? t("userInfo.changePasswordSubmit")
                          : t("userInfo.setPasswordSubmit")}
                      </Button>
                      <Button
                        type="button"
                        variant="subtle"
                        color="charcoal"
                        disabled={passwordMutation.isPending}
                        onClick={() => {
                          setPasswordFormOpen(false);
                          setCurrentPassword("");
                          setNewPassword("");
                          setPasswordMsg(null);
                        }}
                      >
                        {t("userInfo.passwordFormCancel")}
                      </Button>
                    </Group>
                  </Stack>
                </form>
              )}
            </Box>

            <Box
              p="xl"
              style={{
                background: "white",
                border: "1px solid var(--mantine-color-gray-2)",
                borderRadius: "var(--mantine-radius-md)",
                boxShadow: "0 8px 32px rgba(44, 45, 48, 0.06)",
              }}
            >
              <Title order={3} c="charcoal.8" mb="md">
                {t("userInfo.quickLinks")}
              </Title>
              <Stack gap="sm">
                {isDistributor() && (
                  <Anchor
                    component={Link}
                    to="/distributorportal"
                    c="brand.7"
                    fw={600}
                  >
                    {t("userInfo.openDistributorPortal")}
                  </Anchor>
                )}
                {user.IsAdmin && (
                  <Anchor component={Link} to="/admin" c="brand.7" fw={600}>
                    {t("userInfo.admin")}
                  </Anchor>
                )}
                {showTutorials && (
                  <Anchor component={Link} to="/tutorials" c="charcoal.5" fw={500}>
                    {t("nav.tutorials")}
                  </Anchor>
                )}
                <Anchor component={Link} to="/install" c="charcoal.5" fw={500}>
                  {t("nav.download")}
                </Anchor>
                {!user.Bought && (
                  <Anchor component={Link} to="/buy" c="charcoal.5" fw={500}>
                    {t("userInfo.buyApp")}
                  </Anchor>
                )}
              </Stack>
            </Box>
          </Stack>
        </Container>
      </Box>
    </NavBarContainer>
  );
}
